/**
 * `/app/projects` — everything you have made, wherever it lives.
 *
 * `site-skeleton.md` wants this at `/app`, with the editor moving to
 * `/app/e/:id`. The editor currently occupies `/app`, and moving it is the
 * shell fork's call — so this lands beside it for now and consolidates later.
 *
 * The page where three things that have existed for weeks finally have a
 * caller: `readOnly` from the downgrade work, `quotaPrompt()` from the free
 * tier, and `planMigration()` from `shared/migration.ts`.
 */
import { GARMENT_NAMES } from "../../shared/format";
import { formatBytes } from "../../shared/tiers";
import { api } from "../api";
import { explainSaveFailure } from "../api/projects";
import { identity, onIdentityChange, refresh } from "../auth/session";
import { openSignInSheet } from "../auth/sign-in-sheet";
import { faceFor } from "./avatar";
import { forgetProjectId } from "./current-project";
import { deviceLabel } from "./device-label";
import { actionButton, el, formatRelative, replace } from "./dom";
import {
  deleteLocalProject,
  listLocalProjects,
  markMigrated,
  readLocalMeta,
  readLocalProject,
} from "./local-store";
import { announce } from "./project-events";
import { mergeProjects, type ProjectEntry, quotaLine } from "./projects-view";
import { quotaPrompt } from "./upgrade-panel";

export interface ProjectsPanel {
  el: HTMLElement;
  destroy(): void;
}

export function createProjectsPanel(): ProjectsPanel {
  const root = el("div", { class: "hud-projects" });
  const status = el("p", { class: "hud-settings__status", hidden: true, aria: { live: "polite" } });
  const head = el("div", { class: "hud-projects__head" });
  const grid = el("ul", { class: "hud-projects__grid" });
  const abort = new AbortController();
  let ready = false;
  /**
   * Uploads in flight, by design.
   *
   * `actionButton` guards a single button against a double-fire, but a slow
   * upload leaves the card on screen and re-rendering the list rebuilds the
   * button — so the second press lands on a different element with no memory of
   * the first. Two presses would create two cloud projects from one design and
   * spend two of a free plan's three.
   */
  const claiming = new Set<string>();

  replace(root, status, head, grid);

  function say(text: string, tone = "info") {
    status.textContent = text;
    status.dataset.tone = tone;
    status.hidden = text === "";
  }

  function card(entry: ProjectEntry): HTMLElement {
    /*
     * The thumbnail slot exists before thumbnails do. An empty frame keeps the
     * grid honest about its shape rather than reflowing when they land.
     *
     * Tinted from the project's own id, because three identical grey rectangles
     * at 585:559 are the largest thing on the page and read as a page that
     * failed to load. The tint is the same face palette the avatars use, mixed
     * far enough down that it stays a placeholder — and it makes a design
     * recognisable at a glance, which is the job a thumbnail will take over.
     */
    const thumb = el("div", { class: "hud-project__thumb", aria: { hidden: "true" } });
    // 7%, not more: the face palette is bright and saturated, and mixing much
    // of it into a dark surface produces olive and mud rather than a tint.
    thumb.style.background = `color-mix(in oklab, ${faceFor(entry.id)} 7%, var(--hud-item-bg-hover))`;

    /*
     * The real picture, when this device has one.
     *
     * The tint stays underneath rather than being replaced: it is what shows
     * while the object URL resolves, and what remains for a design saved before
     * thumbnails existed or one that lives only in the account. A card never
     * flashes empty.
     *
     * The URL is revoked when the card is replaced — the list re-renders on
     * every load, and a leaked object URL per card per render is a whole
     * thumbnail held for the life of the page.
     */
    if (entry.thumb) {
      const url = URL.createObjectURL(new Blob([entry.thumb as BlobPart], { type: "image/webp" }));
      // `el()` has no `alt`, and this image is decorative — the title beside it
      // is the label — so an empty alt is exactly right and must be explicit.
      const img = document.createElement("img");
      img.className = "hud-project__shot";
      img.alt = "";
      img.src = url;
      img.addEventListener("load", () => URL.revokeObjectURL(url), { once: true });
      img.addEventListener("error", () => URL.revokeObjectURL(url), { once: true });
      thumb.appendChild(img);
    }

    const meta = el(
      "div",
      { class: "hud-project__meta" },
      el("span", { class: "hud-project__title", text: entry.title }),
      el("span", {
        class: "hud-project__detail",
        /*
         * The garment leads, because it is now the most distinguishing thing
         * about a design and the only one nothing else on the card can show.
         * Shirt and pants share a template, so two designs can be pixel-for-
         * pixel identical and still be different garments.
         *
         * Omitted rather than guessed when the archive could not be read — the
         * card says less instead of saying something wrong.
         */
        text: [
          entry.garment ? GARMENT_NAMES[entry.garment] : null,
          entry.kind === "local"
            ? `On this device · ${formatBytes(entry.sizeBytes)} · ${formatRelative(entry.updatedAt)}`
            : `Edited ${formatRelative(entry.updatedAt)}`,
        ]
          .filter(Boolean)
          .join(" · "),
      }),
      // Read-only is a state, not a failure. It is the one badge that earns its
      // place here, because nothing else on the card can show it.
      entry.kind === "cloud" && entry.readOnly
        ? el("span", { class: "hud-project__locked", text: "Read-only on your plan" })
        : null,
      /*
       * A design whose account copy is gone.
       *
       * It is on this device and nowhere else, and it is here rather than
       * hidden because hidden it can never be opened, exported or deleted. But
       * unlabelled it reads as a design that appeared from nowhere — somebody
       * who deleted a design on their laptop and then opened this page would
       * see two drafts they did not make. Saying what it is turns a mystery
       * back into a file.
       */
      entry.kind === "local" && entry.stranded
        ? el("span", {
            class: "hud-project__locked",
            text: "Not in your account — this device only",
          })
        : null,
    );

    /*
     * Where the editor opens this design.
     *
     * Every row can be opened now. The copy on this device wins when there is
     * one — it needs no network and cannot half-arrive — and `?c=` fetches the
     * archive from the account for a design made on another machine, which is
     * the case the page has been promising since autosave landed and could not
     * actually do.
     */
    const localCopy = entry.kind === "local" ? entry.id : entry.localId;
    const openHref = localCopy
      ? `/app?p=${encodeURIComponent(localCopy)}`
      : `/app?c=${encodeURIComponent(entry.id)}`;

    const actions = el(
      "div",
      { class: "hud-project__actions" },
      el("a", { class: "hud-btn hud-btn--go", href: openHref, text: "Open" }),
      entry.kind === "local" && entry.claimable
        ? actionButton("Save to your account", () => claim(entry.id), { class: "hud-btn" })
        : null,
      actionButton("Delete", () => remove(entry), { class: "hud-btn" }),
    );

    return el("li", { class: "hud-project", data: { kind: entry.kind } }, thumb, meta, actions);
  }

  /**
   * Put a design that only exists on this device into the account.
   *
   * The archive on disk is already exactly the bytes the server wants — one
   * `.rbxcloth`, the same format the editor writes and the same one `export`
   * produces — so this is a create and an upload, with no packing and no
   * conversion in between.
   *
   * `markMigrated` last, and only after the upload lands. Recording the
   * migration before the bytes arrive would leave a local design pointing at an
   * empty cloud project, and `mergeProjects` hides a migrated local behind its
   * cloud row — so a failure between the two would replace a design with a
   * blank one.
   */
  async function claim(localId: string) {
    if (claiming.has(localId)) return;
    claiming.add(localId);
    say("Saving to your account…", "info");
    try {
      const id = identity();
      const userId = id.user?.id ?? id.devUser;
      if (!userId) throw new Error("not signed in");

      const [bytes, meta] = await Promise.all([readLocalProject(localId), readLocalMeta(localId)]);
      // Deleted from under us — another tab, or this one before the list was
      // last read.
      if (!bytes) throw new Error("that design is no longer on this device");

      // The garment comes from the row, which `listLocalProjects` fills in from
      // the archive's own manifest when it predates the field.
      const garment = meta?.garment ?? "shirt";
      const project = await api.projects.createProject(
        // The fallback names the garment too. It read "Untitled shirt" for
        // everything, so a nameless pair of pants arrived in the account
        // calling itself a shirt.
        meta?.title ?? `Untitled ${GARMENT_NAMES[garment].toLowerCase()}`,
        garment,
      );
      await api.projects.saveVersion(project.id, bytes, project.revision, deviceLabel());
      await markMigrated(localId, { userId, projectId: project.id });

      say("Saved to your account.", "ok");
      await load();
    } catch (err) {
      say(explainSaveFailure(err), "error");
    } finally {
      claiming.delete(localId);
    }
  }

  /**
   * Delete the design, not one of its copies.
   *
   * A design that has been uploaded exists twice — the archive on this device
   * and the project in the account — and the list shows it once. Deleting only
   * the half the row happens to be named after leaves the other half behind:
   * deleting a cloud row used to leave its local archive in IndexedDB with
   * `migratedTo` still set, which `mergeProjects` hides. The bytes stayed on
   * the device forever, invisible, with no control anywhere that could reach
   * them — a delete that silently kept the thing it deleted.
   *
   * The local copy goes first. If the server call then fails the row is still
   * listed and can be deleted again, which is recoverable; the reverse leaves
   * the invisible orphan this exists to prevent.
   */
  async function remove(entry: ProjectEntry) {
    say("");
    const localId = entry.kind === "local" ? entry.id : entry.localId;
    try {
      if (localId) {
        await deleteLocalProject(localId);
        // The editor resumes whatever this points at. Left aimed at a deleted
        // design, the next visit opens a blank editor under the dead id and the
        // first stroke writes the design back — a delete that undoes itself.
        forgetProjectId(localId);
        // And the same delete, for an editor that has it open right now in
        // another tab. That tab holds the id in memory, where clearing the
        // pointer cannot reach it.
        announce({ kind: "deleted", id: localId });
      }
      if (entry.kind === "cloud") await api.projects.deleteProject(entry.id);
      await load();
    } catch (err) {
      say(explainSaveFailure(err), "error");
      // Whatever half succeeded is already gone, so the list has to be re-read
      // or it keeps offering a row that is no longer there.
      await load().catch(() => {});
    }
  }

  /**
   * Nothing here yet.
   *
   * An empty state that only reports emptiness is a dead end. This one names
   * the single next action, which on this page is always the same one.
   */
  function empty(): HTMLElement {
    // `?new`, not `/app`. A bare `/app` resumes whatever this browser was last
    // editing, which for an empty account is nothing — but the same link is one
    // save away from reopening an existing design instead of starting one.
    const start = el("a", {
      class: "hud-btn hud-btn--go",
      href: "/app?new",
      text: "Start a design",
    });
    return el(
      "li",
      { class: "hud-projects__empty" },
      el("p", { class: "hud-section__note", text: "Nothing here yet." }),
      start,
    );
  }

  async function load() {
    const id = identity();
    const signedIn = id.signedIn || Boolean(id.devUser);

    // Local always resolves — a browser refusing storage is a missing list, not
    // a broken page.
    const local = await listLocalProjects().catch(() => []);
    let cloud: Awaited<ReturnType<typeof api.projects.listProjects>> | null = null;
    if (signedIn) {
      cloud = await api.projects.listProjects(abort.signal).catch(() => null);
    }

    const entries = mergeProjects(
      cloud?.projects ?? [],
      local,
      signedIn ? (id.user?.id ?? id.devUser) : null,
    );

    const cloudCount = cloud?.projects.length ?? 0;
    const line = cloud ? quotaLine(cloud.tier, cloudCount) : null;

    replace(
      head,
      el(
        "div",
        { class: "hud-projects__headings" },
        line ? el("p", { class: "hud-projects__quota", text: line.text }) : null,
        /*
         * Now a promise about frequency, because it is finally one we keep.
         *
         * This deliberately said only where work was kept, never that it saved
         * itself: the editor had no save controller, and reassurance about data
         * safety is the worst place to be optimistic — the same failure as the
         * deletion copy that promised a 30-day grace period that did not exist.
         * The note said to swap it the day cloud save was wired. It is wired,
         * autosave writes on every edit, so the sentence can say so.
         */
        el("p", {
          class: "hud-projects__assurance",
          text: signedIn
            ? "Designs save as you work, on this device and in your account, so they follow you to any computer you sign in to."
            : "Designs save as you work, on this device. Sign in to keep them across computers.",
        }),
        // The earned upgrade moment: shown at the ceiling, never as a wall.
        line?.full
          ? el("p", { class: "hud-section__note", text: quotaPrompt(cloudCount, cloudCount) })
          : null,
      ),
      /*
       * The way to start a second design.
       *
       * Until autosave existed, `/app` was always a blank editor and this was
       * not needed. Now `/app` resumes whatever you had open, so without an
       * explicit `?new` every design on this device funnels into the one the
       * browser last remembered — and there is no way to make another.
       *
       * In the head rather than only in the empty state, because the moment you
       * want a second design is exactly the moment the empty state is gone.
       */
      el("a", { class: "hud-btn hud-btn--go", href: "/app?new", text: "New design" }),
      signedIn
        ? null
        : actionButton(
            "Sign in",
            async () => {
              await openSignInSheet({
                reason: "Sign in",
                kind: "none",
                returnTo: window.location.pathname,
              });
            },
            { class: "hud-btn" },
          ),
    );

    replace(grid, ...(entries.length ? entries.map(card) : [empty()]));
  }

  const stop = onIdentityChange(() => {
    if (ready) void load();
  });

  void refresh().then(() => {
    ready = true;
    void load();
  });

  return {
    el: root,
    destroy() {
      stop();
      abort.abort();
      root.remove();
    },
  };
}
